import { CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";
import { Bar, PageHeader, Section, Spinner } from "@/components/kit";
import { ResourceTable } from "@/components/ResourceTable";
import { useWsData } from "@/hooks/useWsData";
import { api, brl, dateBR, errMsg } from "@/lib/api";

const GOAL_FIELDS = [
  { name: "title", label: "Meta", wide: true },
  { name: "target", label: "Valor alvo (R$)", type: "number" },
  { name: "current", label: "Valor atual (R$)", type: "number", default: 0 },
  { name: "deadline", label: "Prazo", type: "date" },
  { name: "horizon", label: "Horizonte", type: "select", default: "curto", options: [{ value: "curto", label: "Curto (até 1 ano)" }, { value: "medio", label: "Médio (1–5 anos)" }, { value: "longo", label: "Longo (5+ anos)" }] },
];
const TASK_FIELDS = [
  { name: "month", label: "Mês do plano (1–12)", type: "number", default: 1 },
  { name: "title", label: "Tarefa", wide: true },
  { name: "action", label: "Ação detalhada", wide: true, required: false },
];
const MONTHS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

function monthsLeft(deadline) {
  const d = new Date(deadline);
  const n = new Date();
  return Math.max(1, (d.getFullYear() - n.getFullYear()) * 12 + d.getMonth() - n.getMonth());
}

function Timeline({ tasks, wid, reload }) {
  const toggle = async (t) => {
    try { await api.patch(`/workspaces/${wid}/tasks/${t.id}/toggle`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const start = new Date().getMonth();
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="task-timeline">
      {tasks.map((t) => (
        <button key={t.id} onClick={() => toggle(t)} data-testid={`task-toggle-${t.id}`}
          className={`flex gap-3 rounded-xl border p-4 text-left transition-colors ${t.done ? "border-emerald-500/40 bg-emerald-500/10" : "border-[#1E3A6E] bg-[#0B1A33] hover:border-[#D4AF37]/50"}`}>
          {t.done ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" /> : <Circle className="mt-0.5 h-5 w-5 shrink-0 text-slate-500" />}
          <div>
            <div className="text-xs uppercase tracking-wider text-[#D4AF37]">Mês {t.month} · {MONTHS[(start + t.month - 1) % 12]}</div>
            <div className={`mt-1 font-medium ${t.done ? "line-through opacity-70" : ""}`}>{t.title}</div>
            {t.action && <div className="mt-1 text-xs text-slate-400">{t.action}</div>}
          </div>
        </button>
      ))}
    </div>
  );
}

export default function MetasTarefas() {
  const { data: goals, reload: rg, wid } = useWsData("/goals");
  const { data: tasks, reload: rt } = useWsData("/tasks");
  if (!goals || !tasks) return <Spinner />;
  const done = tasks.filter((t) => t.done).length;
  return (
    <div data-testid="metas-page">
      <PageHeader eyebrow="Acompanhamento" title="Metas e cronograma mensal" subtitle="Defina metas com prazo, veja o aporte necessário e marque as etapas do seu plano de 12 meses." />
      <Section className="mb-6">
        <ResourceTable wid={wid} res="goals" title="Metas" addLabel="Nova meta" rows={goals} onChanged={rg} fields={GOAL_FIELDS}
          columns={[{ key: "title", label: "Meta" }, { key: "progress", label: "Progresso", render: (g) => <div className="w-40"><Bar value={(g.current / g.target) * 100} color="#D4AF37" /><span className="num text-xs text-slate-400">{Math.round((g.current / g.target) * 100)}%</span></div> },
            { key: "target", label: "Alvo", right: true, render: (g) => brl(g.target) }, { key: "deadline", label: "Prazo", render: (g) => dateBR(g.deadline) },
            { key: "monthly", label: "Aporte/mês necessário", right: true, render: (g) => brl(Math.max(0, g.target - g.current) / monthsLeft(g.deadline)) }]} />
      </Section>
      <Section title={`Cronograma de 12 meses · ${done}/${tasks.length} concluídas`} testid="timeline-section">
        <div className="mb-5"><Bar value={tasks.length ? (done / tasks.length) * 100 : 0} /></div>
        {tasks.length ? <Timeline tasks={tasks} wid={wid} reload={rt} /> : <p className="text-sm text-slate-400">Faça o diagnóstico na Visão geral para gerar o cronograma automaticamente.</p>}
        <div className="mt-8">
          <ResourceTable wid={wid} res="tasks" title="Gerenciar tarefas" addLabel="Nova tarefa" rows={tasks} onChanged={rt} fields={TASK_FIELDS}
            mapEdit={(t) => t} columns={[{ key: "month", label: "Mês" }, { key: "title", label: "Tarefa" }, { key: "done", label: "Status", render: (t) => (t.done ? "Concluída" : "Pendente") }]} />
        </div>
      </Section>
    </div>
  );
}
